import React, { useLayoutEffect, useMemo, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { useAuthStore } from 'api/swr'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { AvatarIcon, ChevronRightIcon } from '@radix-ui/react-icons'
import { usePermissions } from 'hooks/useProCptPermission'
import Navbar from 'components/Navbar'
import UpdateAnnouncements from './UpdateAnnouncements'

const ADMIN_GUIDE_DOC_URL =
  'https://stickerstyle.feishu.cn/docx/UutqdtTelo37aFx694JcCsNFnSb'

const NAV_ITEMS = [
  { href: '/admin', name: 'Templates', end: true },
  { href: '/admin/avatars', name: 'Avatars' },
  { href: '/admin/ocs', name: 'Ocs' },
  { href: '/admin/spine', name: 'Spine Templates' },
  { href: '/admin/spine-avatars', name: 'Spine Avatars' },
  { href: '/admin/webimgs', name: 'WebImages' },
  { href: '/admin/publishers', name: 'Painters' },
  { href: '/admin/comm', name: 'Comm' },
  { href: '/admin/search', name: 'Search' },
  { href: '/admin/template-lists', name: 'Template Lists' },
  { href: '/admin/app-lists', name: 'App Lists' },
  { href: '/admin/ops-tools', name: '运营工具' },
  { href: '/admin/dev-tools', name: 'Dev Tools' },
]

const navLinkClass = ({ isActive }) =>
  cn('whitespace-nowrap transition-colors', {
    'font-bold': isActive,
    'text-foreground/60': !isActive,
  })

const NavItem = ({ href, name, end }) => (
  <NavLink to={href} end={end} className={navLinkClass}>
    {name}
  </NavLink>
)

const isItemActive = (pathname, item) => {
  if (item.end) {
    return pathname === item.href
  }
  return pathname === item.href || pathname.startsWith(`${item.href}/`)
}

const MORE_BUTTON_WIDTH = 36

const useVisibleNavCount = (containerRef, measureRef) => {
  const [visibleCount, setVisibleCount] = useState(NAV_ITEMS.length)

  useLayoutEffect(() => {
    const container = containerRef.current
    const measureEl = measureRef.current
    if (!container || !measureEl) return

    const calc = () => {
      const children = Array.from(measureEl.children)
      if (!children.length) return

      const containerWidth = container.clientWidth
      const gapValue = getComputedStyle(measureEl).columnGap
      const gap = gapValue ? parseFloat(gapValue) || 16 : 16
      const moreWidth = MORE_BUTTON_WIDTH
      let used = 0
      let count = 0

      for (let i = 0; i < children.length; i += 1) {
        const itemWidth = children[i].offsetWidth
        const gapBefore = count > 0 ? gap : 0
        const itemsAfter = children.length - i - 1
        const reserveMore = itemsAfter > 0 ? gap + moreWidth : 0
        const nextUsed = used + gapBefore + itemWidth

        if (nextUsed + reserveMore <= containerWidth) {
          used = nextUsed
          count = i + 1
        } else {
          break
        }
      }

      setVisibleCount(Math.max(0, count))
    }

    calc()

    const observer = new ResizeObserver(calc)
    observer.observe(container)

    window.addEventListener('resize', calc)
    return () => {
      observer.disconnect()
      window.removeEventListener('resize', calc)
    }
  }, [containerRef, measureRef])

  return visibleCount
}

const Nav = () => {
  const user = useAuthStore((state) => state.auth?.user)
  const face = useAuthStore((state) => state.auth?.face)
  const logout = useAuthStore((state) => state.removeToken)
  const location = useLocation()
  const { can } = usePermissions()
  const canSendUpdateAnnouncement = can('sendUpdateAnnouncement')

  const containerRef = useRef(null)
  const measureRef = useRef(null)
  const visibleCount = useVisibleNavCount(containerRef, measureRef)

  const overflowItems = NAV_ITEMS.slice(visibleCount)
  const visibleItems = NAV_ITEMS.slice(0, visibleCount)
  const hasOverflow = overflowItems.length > 0

  const overflowActive = useMemo(
    () => overflowItems.some((item) => isItemActive(location.pathname, item)),
    [location.pathname, overflowItems],
  )

  if (!user || !face) {
    return null
  }

  return (
    <Navbar name="Neku" sticky>
      <div ref={containerRef} className="relative min-w-0 flex-1 overflow-hidden">
        <div
          ref={measureRef}
          aria-hidden
          className="pointer-events-none absolute left-0 top-0 -z-10 flex items-center gap-4 opacity-0 lg:gap-6"
        >
          {NAV_ITEMS.map((item) => (
            <span key={item.href} className="whitespace-nowrap">
              {item.name}
            </span>
          ))}
        </div>

        <div className="flex min-w-0 items-center gap-4 overflow-hidden lg:gap-6">
          {visibleItems.map((item) => (
            <NavItem key={item.href} {...item} />
          ))}

          {hasOverflow ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className={cn('h-8 w-9 shrink-0 px-2', {
                    'font-bold text-foreground': overflowActive,
                    'text-foreground/60': !overflowActive,
                  })}
                  aria-label="更多菜单"
                >
                  <ChevronRightIcon className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[180px]">
                {overflowItems.map((item) => (
                  <DropdownMenuItem key={item.href} asChild>
                    <NavLink
                      to={item.href}
                      end={item.end}
                      className={({ isActive }) =>
                        cn('cursor-pointer', {
                          'font-bold': isActive,
                        })
                      }
                    >
                      {item.name}
                    </NavLink>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </div>
      </div>

      <div className="ml-6 flex shrink-0 items-center gap-1">
        <Button variant="ghost" asChild>
          <a
            href={ADMIN_GUIDE_DOC_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="whitespace-nowrap text-primary hover:text-primary/80"
          >
            后台功能说明
          </a>
        </Button>
        <UpdateAnnouncements canPublish={canSendUpdateAnnouncement} />
        <Button variant="ghost" asChild>
          <Link to="/login?error=账号已退出登录" onClick={logout}>
            <AvatarIcon className="mr-2 h-6 w-6" />
            <span className="hidden whitespace-nowrap sm:inline">
              {user.username} Log Out
            </span>
          </Link>
        </Button>
      </div>
    </Navbar>
  )
}

export default Nav
